import { Box, Paper, TextField, Typography } from '@mui/material'

import { DropdownField } from './DropdownField'
import type { ArticleFormState, FormErrors } from '../types/article-form.types'
import type { DropdownOptionsByField } from '../../dropdown/types/dropdown.types'

type ArticleMetadataSectionsProps = {
  dropdownOptions: DropdownOptionsByField
  errors: FormErrors
  form: ArticleFormState
  isCustomerSpecific: boolean
  onRequestOption: (fieldName: string, label: string) => void
  status: string
  setField: <K extends keyof ArticleFormState>(field: K) => (value: ArticleFormState[K]) => void
}

export function ArticleMetadataSections({
  dropdownOptions,
  errors,
  form,
  isCustomerSpecific,
  onRequestOption,
  setField,
  status
}: ArticleMetadataSectionsProps) {
  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" color="primary" mb={0.5}>
          1. General Information
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Core identification fields for the Technote and initial classification.
        </Typography>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
          <TextField
            label="Technote Title"
            fullWidth
            value={form.title}
            onChange={event => setField('title')(event.target.value)}
            error={Boolean(errors.title)}
            helperText={errors.title}
            required={true}
            placeholder="Example: PRISM PLS Firmware Flashing Methods and Considerations"
          />
          <TextField
            label="Technote ID"
            fullWidth
            value="Auto-generated after save"
            InputProps={{ readOnly: true }}
            sx={{ '& input': { bgcolor: '#f8fafc', color: '#475467' } }}
            helperText="System-generated based on discipline and next consecutive number"
          />
          <DropdownField
            label="Discipline"
            fieldName="discipline"
            value={form.discipline}
            onChange={setField('discipline')}
            options={dropdownOptions}
            required
            error={errors.discipline}
            onRequestOption={onRequestOption}
          />
          <DropdownField
            label="Technote Type"
            fieldName="technote_type"
            value={form.technote_type}
            onChange={setField('technote_type')}
            options={dropdownOptions}
            required
            error={errors.technote_type}
            onRequestOption={onRequestOption}
          />
          <DropdownField
            label="Scope"
            fieldName="scope"
            value={form.scope}
            onChange={setField('scope')}
            options={dropdownOptions}
            required
            error={errors.scope}
            onRequestOption={onRequestOption}
          />
          <DropdownField
            label="Customer"
            fieldName="customer"
            value={form.customer}
            onChange={setField('customer')}
            options={dropdownOptions}
            required={isCustomerSpecific}
            error={errors.customer}
            helperText={
              isCustomerSpecific
                ? 'Required when scope is Customer Specific'
                : 'Automatically set to General when scope is not Customer Specific'
            }
            disabled={Boolean(form.scope) && !isCustomerSpecific}
            onRequestOption={onRequestOption}
          />
          <DropdownField
            label="Confidentiality"
            fieldName="confidentiality"
            value={form.confidentiality}
            onChange={setField('confidentiality')}
            options={dropdownOptions}
            required
            error={errors.confidentiality}
            onRequestOption={onRequestOption}
          />
          <TextField
            label="Status"
            fullWidth
            value={status.toUpperCase()}
            InputProps={{ readOnly: true }}
            sx={{ '& input': { bgcolor: '#f8fafc', color: '#475467' } }}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" color="primary" mb={0.5}>
          2. Equipment / Classification
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Controlled metadata used for searchability, filtering, and future automation.
        </Typography>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }} gap={2}>
          <DropdownField
            label="Brand"
            fieldName="brand"
            value={form.brand}
            onChange={setField('brand')}
            options={dropdownOptions}
            required
            error={errors.brand}
            onRequestOption={onRequestOption}
          />
          <TextField
            label="Product Family"
            fullWidth
            value={form.product_family}
            onChange={event => setField('product_family')(event.target.value)}
            error={Boolean(errors.product_family)}
            helperText={errors.product_family}
            placeholder="Example: Gemco Series 7500"
            required={true}
          />
          <TextField
            label="Model"
            fullWidth
            value={form.model_number}
            onChange={event => setField('model_number')(event.target.value)}
            placeholder="Example: 7500"
          />
          <DropdownField
            label="Asset Type"
            fieldName="asset_type"
            value={form.asset_type}
            onChange={setField('asset_type')}
            options={dropdownOptions}
            required
            error={errors.asset_type}
            onRequestOption={onRequestOption}
          />
          <DropdownField
            label="Technology Type"
            fieldName="technology_type"
            value={form.technology_type}
            onChange={setField('technology_type')}
            options={dropdownOptions}
            required
            error={errors.technology_type}
            onRequestOption={onRequestOption}
          />
          <TextField
            label="Machine / Line"
            fullWidth
            value={form.machine}
            onChange={event => setField('machine')(event.target.value)}
            placeholder="Example: CZ12"
          />
          <TextField
            label="Firmware / Software Version"
            fullWidth
            value={form.version}
            onChange={event => setField('version')(event.target.value)}
            placeholder="Example: PLS v4.11 / Anybus v1.35.1+"
            sx={{ gridColumn: { md: 'span 2' } }}
          />
        </Box>
        <Box mt={2} p={1.5} sx={{ bgcolor: '#f8fafc', borderLeft: '4px solid #1f6fb2', borderRadius: 1, color: '#6b7280', fontSize: '0.8125rem' }}>
          Dropdown values come from controlled master data to avoid duplicates and inconsistent naming.
        </Box>
      </Paper>
    </>
  )
}
